import type { PostValidateSubscriptionUse } from "@/server-types";

type ValidationError =
  PostValidateSubscriptionUse["response"]["errors"][number];

export type SubscriptionWarningTone = "danger" | "warning" | "info";

export type SubscriptionWarning =
  | { kind: "validation"; tone: "danger"; error: ValidationError }
  | {
      kind: "outstanding-period";
      tone: "danger" | "warning";
      isPartiallyPaid: boolean;
      periodStartFormatted: string;
      periodEndFormatted: string;
    }
  | { kind: "not-valid-at-appointment-time"; tone: "warning" }
  | { kind: "near-limit"; tone: "warning" }
  | { kind: "expiring-soon"; tone: "info" };

type RankSubscriptionWarningsInput = {
  validationErrors: ValidationError[];
  isNewAppointment: boolean;
  outstandingPeriod: {
    isPartiallyPaid: boolean;
    periodStartFormatted: string;
    periodEndFormatted: string;
  } | null;
  isNotValidAtAppointmentTime: boolean;
  hasLimitationNearLimit: boolean;
  isExpiringSoon: boolean;
};

const VALIDATION_ERROR_RANK: Record<ValidationError["type"], number> = {
  SUBSCRIPTION_INACTIVE: 0,
  SERVICE_NOT_INCLUDED: 1,
  LOCATION_NOT_INCLUDED: 2,
  DATE_INVALID: 3,
  SUBSCRIPTION_FROZEN: 4,
  LIMITATION_EXCEEDED: 5,
};

function getValidationErrorKey(error: ValidationError): string {
  if (error.type === "LIMITATION_EXCEEDED") {
    return `${error.type}:${error.limitation.timeframe}:${error.limitation.limitAmount}`;
  }
  return error.type;
}

/** Most important first; the server reports one error per selected service, so duplicates collapse. */
export function rankSubscriptionWarnings(
  input: RankSubscriptionWarningsInput,
): SubscriptionWarning[] {
  const seenKeys = new Set<string>();
  const validationWarnings = input.validationErrors
    // An edited appointment already counts towards the usage the limit is checked against.
    .filter(
      (error) => input.isNewAppointment || error.type !== "LIMITATION_EXCEEDED",
    )
    .filter((error) => {
      const key = getValidationErrorKey(error);
      if (seenKeys.has(key)) return false;
      seenKeys.add(key);
      return true;
    })
    .sort(
      (a, b) => VALIDATION_ERROR_RANK[a.type] - VALIDATION_ERROR_RANK[b.type],
    )
    .map(
      (error): SubscriptionWarning => ({
        kind: "validation",
        tone: "danger",
        error,
      }),
    );

  const hasDateError = validationWarnings.some(
    (warning) =>
      warning.kind === "validation" && warning.error.type === "DATE_INVALID",
  );

  const warnings: SubscriptionWarning[] = [...validationWarnings];

  if (input.outstandingPeriod) {
    warnings.push({
      kind: "outstanding-period",
      tone: input.outstandingPeriod.isPartiallyPaid ? "warning" : "danger",
      ...input.outstandingPeriod,
    });
  }
  if (input.isNotValidAtAppointmentTime && !hasDateError) {
    warnings.push({ kind: "not-valid-at-appointment-time", tone: "warning" });
  }
  if (input.hasLimitationNearLimit) {
    warnings.push({ kind: "near-limit", tone: "warning" });
  }
  if (input.isExpiringSoon) {
    warnings.push({ kind: "expiring-soon", tone: "info" });
  }

  return warnings;
}
